if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();
      this.productSection = this.closest('.cc-main-product');
      this.addToCartButton = null;
      this._io = null;
      this._mutationObserver = null;
      this._raf = 0;
      this.buttonVisible = true;
      this.onVariantChange = this.onVariantChange.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
    }

    connectedCallback() {
      if (!('IntersectionObserver' in window)) {
        this.classList.add('sticky-atc-panel--out');
        return;
      }
      this.bindObserver();
      document.addEventListener('variant:change', this.onVariantChange);
      document.addEventListener('variant:changed', this.onVariantChange);
      document.addEventListener('on:variant:change', this.onVariantChange);
      setTimeout(() => this.onVariantChange(), 100);
    }

    findMainButton() {
      const root = this.productSection || document;
      return (
        root.querySelector('button[type="submit"][name="add"].w-full') ||
        root.querySelector('button[type="submit"][name="add"]') ||
        root.querySelector('[name="add"]')
      );
    }

    scheduleShow(show) {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => {
        this._raf = 0;
        this.classList.toggle('sticky-atc-panel--out', !show);
      });
    }

    bindObserver() {
      this.addToCartButton = this.findMainButton();
      if (this._io) {
        try {
          this._io.disconnect();
        } catch (e) {}
        this._io = null;
      }

      if (!this.addToCartButton) {
        this.scheduleShow(false);
        return;
      }

      this._io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          this.buttonVisible = entry.isIntersecting && entry.intersectionRatio > 0;
          // Show sticky ATC only when main ATC has left the viewport upward
          const show = !this.buttonVisible && entry.boundingClientRect.top < 0;
          this.scheduleShow(show);
        },
        { root: null, threshold: [0, 0.01, 1], rootMargin: '0px' }
      );
      try {
        this._io.observe(this.addToCartButton);
      } catch (e) {}

      if (!this._mutationObserver) {
        this._mutationObserver = new MutationObserver(this.handleMutations);
        this._mutationObserver.observe(this.productSection || document.body, {
          childList: true,
          subtree: false
        });
      }
    }

    handleMutations() {
      const next = this.findMainButton();
      if (next !== this.addToCartButton) {
        this.bindObserver();
      }
    }

    onVariantChange() {
      try {
        const variantPicker = this.productSection?.querySelector('variant-picker');
        if (!variantPicker || !variantPicker.variant) return;

        const variant = variantPicker.variant;
        const formatted = variantPicker.data?.formatted?.[String(variant.id)] || {};
        const stickyPrice = this.querySelector('.sticky-atc__price');
        if (!stickyPrice) return;

        const priceCurrentEl = stickyPrice.querySelector('.price__current');
        const priceWasEl = stickyPrice.querySelector('.price__was');
        const unitPriceEl = stickyPrice.querySelector('.unit-price');
        if (!priceCurrentEl) return;

        const currentValueEl = priceCurrentEl.querySelector('.js-value');
        const wasValueEl = priceWasEl?.querySelector('.js-value');

        if (variant.compare_at_price > variant.price) {
          if (currentValueEl) currentValueEl.innerHTML = formatted.price || '';
          if (wasValueEl) wasValueEl.innerHTML = formatted.compareAtPrice || '';
          stickyPrice.classList.add('price--on-sale');
        } else {
          if (currentValueEl) currentValueEl.innerHTML = formatted.price || '';
          if (wasValueEl) wasValueEl.innerHTML = '';
          stickyPrice.classList.remove('price--on-sale');
        }

        if (variant.unit_price_measurement && unitPriceEl) {
          const valueEl = unitPriceEl.querySelector('.unit-price__price');
          const unitEl = unitPriceEl.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;
          if (valueEl) valueEl.innerHTML = formatted.unitPrice || '';
          if (unitEl) unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        stickyPrice.classList.toggle('price--sold-out', !variant.available);

        const stickyForm = this.querySelector('.js-product-form-sticky');
        const variantInput = stickyForm?.querySelector('input[name="id"]');
        if (variantInput) variantInput.value = variant.id;

        this.updateStickyButton(variant);
      } catch (error) {
        console.error('Error updating sticky panel:', error);
      }
    }

    updateStickyButton(variant) {
      try {
        const stickyForm = this.querySelector('.js-product-form-sticky');
        if (!stickyForm) return;
        const submitButton = stickyForm.querySelector('button[name="add"]');
        if (!submitButton) return;

        const variantData = window.variantMetafieldData?.[variant.id];
        const badge =
          variantData?.badge_value ||
          variantData?.badge ||
          variantData?.Badge ||
          variantData?.badge_label ||
          null;
        const shouldDisableByBadge =
          badge && ['out of stock', 'discontinued'].includes(String(badge).toLowerCase());
        const isAvailable = variant.available && !shouldDisableByBadge;
        const labelOut = theme.strings?.noStock || 'Sold Out';
        const labelAdd = submitButton.getAttribute('data-add-to-cart-text') || 'Add to cart';

        submitButton.disabled = !isAvailable;
        submitButton.style.opacity = isAvailable ? '1' : '0.5';
        submitButton.style.pointerEvents = isAvailable ? 'auto' : 'none';
        submitButton.textContent = isAvailable ? labelAdd : labelOut;
      } catch (error) {
        console.error('Error updating sticky button:', error);
      }
    }

    disconnectedCallback() {
      try {
        document.removeEventListener('variant:change', this.onVariantChange);
        document.removeEventListener('variant:changed', this.onVariantChange);
        document.removeEventListener('on:variant:change', this.onVariantChange);
      } catch (e) {}
      if (this._io) {
        try {
          this._io.disconnect();
        } catch (e) {}
      }
      if (this._mutationObserver) {
        try {
          this._mutationObserver.disconnect();
        } catch (e) {}
      }
      if (this._raf) cancelAnimationFrame(this._raf);
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
